import { type ReactNode } from 'react'
import { View, Text, type TextStyle, type ViewStyle } from 'react-native'
import { useLocalSearchParams, usePathname } from 'expo-router'
import { BackButton } from './BackButton'
import { FONTS, SPACING } from '@/lib/brand'
import { INTERACTION, TEXT_STYLES, useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'

interface ScreenHeaderProps {
  title?: string
  subtitle?: string
  right?: ReactNode
  showBack?: boolean
  backTestID?: string
  onBackPress?: () => void
  fallbackHref?: string
  testID?: string
  titleTestID?: string
  titleNumberOfLines?: number
  style?: ViewStyle
  titleStyle?: TextStyle
}

/**
 * "Volvé a donde estaba" dicho por quien navega, no adivinado por la pantalla.
 *
 * EL PROBLEMA. Desde la ficha de un carro, tocar "hacelo ahora: foto de tu DUI"
 * lleva a `/profile/verification/identity`, y la flecha de atras aterriza en el
 * tab Perfil: el carro que la persona queria desaparece, y para volver tiene que
 * ir a Explorar, buscarlo otra vez y rehacer las fechas. Medido en el navegador
 * el 2026-08-25. Es exactamente el camino al que empujamos a las 1.057 personas
 * represadas en el telefono.
 *
 * LO QUE NO SIRVIO. Probe dejar que `router.back()` mandara siempre, quitando
 * `preferFallback`. Medido: los TRES casos --viaje cruzado, enlace profundo y
 * retroceso dentro de la misma pestaña-- terminaban en `/explore`. En web la
 * historia de expo-router no es una pila limpia, y ese respaldo estaba
 * protegiendo justamente de eso. Revertido.
 *
 * LO QUE SI. Que el origen viaje en la URL: quien navega agrega `?from=<ruta>` y
 * esta cabecera lo honra como destino de la flecha. No cambia el comportamiento
 * de nadie que no lo mande, y el respaldo por pestaña sigue igual para todos los
 * demas.
 *
 * Solo rutas internas: un `from` con protocolo, con `//` o sin `/` inicial se
 * descarta. Un parametro de URL lo escribe cualquiera, y no va a mandar a nadie
 * fuera de la app.
 */
function safeInternalPath(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const path = value.trim()
  if (!path.startsWith('/')) return null
  if (path.startsWith('//')) return null
  if (/^\/+[a-z][a-z0-9+.-]*:/i.test(path)) return null
  return path
}

/**
 * El mismo saneo que usa la flecha de atrás, expuesto para que una pantalla
 * pueda volver al origen que la URL declara (`?from=`) después de terminar el
 * trámite — no sólo al tocarlo la flecha.
 */
export function safeInternalReturnPath(value: unknown, fallback: string): string {
  return safeInternalPath(value) ?? fallback
}

function getNaturalTabFallback(pathname: string): string | null {
  const [root, child] = pathname.split('/').filter(Boolean)
  if (!child) return null

  if (root === 'profile') return '/(tabs)/profile'
  if (root === 'hosting') return '/(tabs)/hosting'
  if (root === 'trips') return '/(tabs)/trips'
  if (root === 'explore') return '/(tabs)/explore'
  if (root === 'messages') return '/(tabs)/messages'

  return null
}

export function ScreenHeader({
  title,
  subtitle,
  right,
  showBack = true,
  backTestID,
  onBackPress,
  fallbackHref,
  testID,
  titleTestID,
  titleNumberOfLines = 2,
  style,
  titleStyle,
}: ScreenHeaderProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const pathname = usePathname()
  const { from } = useLocalSearchParams<{ from?: string }>()
  const naturalFallback = fallbackHref ?? safeInternalPath(from) ?? getNaturalTabFallback(pathname)

  return (
    <View
      testID={testID}
      style={[
        {
          paddingHorizontal: SPACING.lg,
          // Symmetric with the padding below: the header used to sit 8pt under
          // the safe area with 16pt beneath it, so the title crowded the top
          // edge of every screen while the gap under it was twice as large.
          paddingTop: SPACING.md,
          paddingBottom: SPACING.md,
          gap: SPACING.sm,
        },
        style,
      ]}
    >
      {/* The back button aligns with the title, and the subtitle sits below
          the whole row at full width.
          Before, the subtitle lived in the title's column: the back button was
          centred against a two-line block so it floated between them, and the
          subtitle wrapped inside a column that started 170pt in, leaving that
          much empty under the button.
          The row is top-aligned and every cell is at least one touch target
          tall: with a one-line title everything centres exactly as before, and
          when the title wraps the button stays on the FIRST line instead of
          drifting down to the middle of the block. */}
      <View
        style={{
          minHeight: INTERACTION.minTouchTarget,
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: SPACING.md,
        }}
      >
        {showBack ? (
          <BackButton
            onPress={onBackPress}
            fallbackHref={naturalFallback ?? undefined}
            preferFallback={Boolean(naturalFallback)}
            testID={backTestID}
          />
        ) : null}
        {title ? (
          <View
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: INTERACTION.minTouchTarget,
              justifyContent: 'center',
            }}
          >
            <Text
              testID={titleTestID}
              accessibilityRole="header"
              numberOfLines={titleNumberOfLines}
              adjustsFontSizeToFit
              minimumFontScale={0.86}
              style={[
                {
                  ...TEXT_STYLES.h3,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  letterSpacing: 0,
                },
                titleStyle,
              ]}
            >
              {title}
            </Text>
          </View>
        ) : (
          <View style={{ flex: 1, minHeight: INTERACTION.minTouchTarget }} />
        )}
        {right ? (
          <View
            style={{
              minHeight: INTERACTION.minTouchTarget,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: SPACING.sm,
            }}
          >
            {right}
          </View>
        ) : null}
      </View>

      {subtitle ? (
        <Text
          numberOfLines={3}
          style={{
            fontSize: 14,
            lineHeight: 20,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
        >
          {subtitle}
        </Text>
      ) : null}
    </View>
  )
}
